import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * La cámara.
 *
 * Cuatro cosas que se aprenden probando en un móvil de verdad y no en el
 * escritorio:
 *
 * 1. **El permiso se pide por origen y iOS no lo recuerda entre Safari y la
 *    PWA instalada.** Por eso el error trae un texto de ayuda distinto según
 *    el sistema y según si está instalada.
 * 2. **Hay que soltar el stream al salir.** Si no, el led de la cámara se
 *    queda encendido, el móvil se calienta y la batería se va. En iOS además
 *    la siguiente pantalla que pida cámara falla.
 * 3. **`facingMode: 'environment'` no basta**: en algunos Android hay que
 *    pedirlo como `ideal` o falla en móviles con tres cámaras traseras.
 * 4. **La resolución se pide, no se impone.** Con `exact` falla en cuanto un
 *    móvil no la tiene; con `ideal` el navegador da lo más parecido.
 */

export type EstadoCamara = 'inactiva' | 'pidiendo' | 'lista' | 'denegada' | 'sin_camara' | 'error';

export interface Camara {
    estado: EstadoCamara;
    stream: MediaStream | null;
    error: string | null;
    encender: () => Promise<void>;
    apagar: () => void;
    linterna: (on: boolean) => Promise<boolean>;
    tieneLinterna: boolean;
}

const ALTURAS: Record<'480p' | '720p' | '1080p', number> = {
    '480p': 480,
    '720p': 720,
    '1080p': 1080,
};

export function useCamara(calidad: '480p' | '720p' | '1080p' = '720p', conAudio = false): Camara {
    const [estado, setEstado] = useState<EstadoCamara>('inactiva');
    const [error, setError] = useState<string | null>(null);
    const [tieneLinterna, setTieneLinterna] = useState(false);
    const streamRef = useRef<MediaStream | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);

    const apagar = useCallback(() => {
        for (const t of streamRef.current?.getTracks() ?? []) {
            t.stop();
        }
        streamRef.current = null;
        setStream(null);
        setEstado('inactiva');
    }, []);

    const encender = useCallback(async () => {
        if (navigator.mediaDevices?.getUserMedia === undefined) {
            setEstado('sin_camara');

            return;
        }
        if (streamRef.current !== null) {
            return;
        }

        setEstado('pidiendo');
        setError(null);

        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: 'environment' },
                    height: { ideal: ALTURAS[calidad] },
                    frameRate: { ideal: 30, max: 30 },
                },
                // Audio a propósito en el vídeo: si algo se oye —una cisterna
                // corriendo, un aviso de la empleada— forma parte de la prueba.
                audio: conAudio,
            });

            streamRef.current = s;
            setStream(s);
            setEstado('lista');

            const pista = s.getVideoTracks()[0];
            const capacidades = pista?.getCapabilities?.() as { torch?: boolean } | undefined;
            setTieneLinterna(capacidades?.torch === true);
        } catch (e) {
            const nombre = e instanceof DOMException ? e.name : '';
            setError(nombre);
            setEstado(
                nombre === 'NotAllowedError' || nombre === 'SecurityError'
                    ? 'denegada'
                    : nombre === 'NotFoundError'
                      ? 'sin_camara'
                      : 'error'
            );
        }
    }, [calidad, conAudio]);

    const linterna = useCallback(async (on: boolean) => {
        const pista = streamRef.current?.getVideoTracks()[0];
        if (pista === undefined) {
            return false;
        }
        try {
            await pista.applyConstraints({
                advanced: [{ torch: on } as unknown as MediaTrackConstraintSet],
            });

            return true;
        } catch {
            return false;
        }
    }, []);

    // Soltar la cámara al desmontar. Sin esto el led se queda encendido.
    useEffect(() => apagar, [apagar]);

    return { estado, stream, error, encender, apagar, linterna, tieneLinterna };
}
